/**
 * Debug messenger (F5 / group 180 trắng).
 * Bật: localStorage.setItem('SOCIAL_DEBUG_MESSENGER', '1') rồi F5
 * Hoặc URL: ?debugMessenger=1
 * Xuất log: __dumpMessengerDebug() trong Console
 */
const STORAGE_KEY = 'SOCIAL_DEBUG_MESSENGER';
const BUFFER_KEY = 'SOCIAL_DEBUG_MESSENGER_BUF';
const MAX_BUFFER = 250;
export function isMessengerDebugEnabled() {
    if (typeof window === 'undefined') {
        return false;
    }
    try {
        if (localStorage.getItem(STORAGE_KEY) === '1') {
            return true;
        }
        return /(?:\?|&)debugMessenger=1(?:&|$)/.test(window.location.search);
    }
    catch {
        return false;
    }
}
function sanitizeForLog(value, depth = 0) {
    if (depth > 4) {
        return '[max-depth]';
    }
    if (value == null || typeof value !== 'object') {
        return value;
    }
    if (Array.isArray(value)) {
        if (value.length > 8) {
            return {
                _array: true,
                length: value.length,
                sample: value.slice(0, 3).map((v) => sanitizeForLog(v, depth + 1)),
            };
        }
        return value.map((v) => sanitizeForLog(v, depth + 1));
    }
    const obj = value;
    const out = {};
    const keys = Object.keys(obj).slice(0, 40);
    for (const k of keys) {
        const v = obj[k];
        if (typeof v === 'string' && v.length > 500) {
            out[k] = `${v.slice(0, 500)}…`;
        }
        else {
            out[k] = sanitizeForLog(v, depth + 1);
        }
    }
    if (Object.keys(obj).length > keys.length) {
        out._truncatedKeys = Object.keys(obj).length - keys.length;
    }
    return out;
}
export function messengerDebugLog(phase, payload) {
    if (!isMessengerDebugEnabled()) {
        return;
    }
    const entry = {
        ts: new Date().toISOString(),
        phase,
        ...(payload ? { payload: sanitizeForLog(payload) } : {}),
    };
    // eslint-disable-next-line no-console
    console.log(`[messenger:debug] ${phase}`, entry.payload ?? '');
    try {
        const raw = sessionStorage.getItem(BUFFER_KEY);
        const buf = raw ? JSON.parse(raw) : [];
        buf.push(entry);
        while (buf.length > MAX_BUFFER) {
            buf.shift();
        }
        sessionStorage.setItem(BUFFER_KEY, JSON.stringify(buf));
    }
    catch {
        /* ignore quota */
    }
}
export function dumpMessengerDebugBuffer() {
    try {
        const raw = sessionStorage.getItem(BUFFER_KEY);
        return raw ? JSON.parse(raw) : [];
    }
    catch {
        return [];
    }
}
export function clearMessengerDebugBuffer() {
    try {
        sessionStorage.removeItem(BUFFER_KEY);
    }
    catch {
        /* noop */
    }
}
export function installMessengerDebugGlobals() {
    if (typeof window === 'undefined') {
        return;
    }
    if (isMessengerDebugEnabled()) {
        try {
            localStorage.setItem(STORAGE_KEY, '1');
        }
        catch {
            /* noop */
        }
    }
    const w = window;
    w.__enableMessengerDebug = () => {
        localStorage.setItem(STORAGE_KEY, '1');
        clearMessengerDebugBuffer();
        messengerDebugLog('debug.enabled', { hint: 'F5 trang messenger rồi gọi __dumpMessengerDebug()' });
    };
    w.__disableMessengerDebug = () => {
        localStorage.removeItem(STORAGE_KEY);
    };
    w.__dumpMessengerDebug = () => {
        const buf = dumpMessengerDebugBuffer();
        // eslint-disable-next-line no-console
        console.log('[messenger:debug] buffer dump (%s entries)', buf.length, buf);
        if (!buf.length) {
            // eslint-disable-next-line no-console
            console.warn('[messenger:debug] Buffer trống. Bật: __enableMessengerDebug() hoặc ?debugMessenger=1 rồi F5. Lưu ý: copy(...) trả về undefined là bình thường — dùng biến: var d = __dumpMessengerDebug()');
        }
        return buf;
    };
    w.__clearMessengerDebug = () => clearMessengerDebugBuffer();
}
/** Chuẩn hóa body GET /chat/groups/message (paginate hoặc bọc { status, data }). */
export function normalizeChatMessageListResponse(res) {
    if (!res || typeof res !== 'object') {
        return { rows: [], meta: null, peerReadMessageId: undefined, shape: 'null' };
    }
    const root = res;
    let shape = 'root';
    const pickPeer = (obj) => {
        if (Object.prototype.hasOwnProperty.call(obj, 'peer_read_message_id')) {
            return obj.peer_read_message_id;
        }
        const meta = obj.meta;
        if (meta && typeof meta === 'object' && Object.prototype.hasOwnProperty.call(meta, 'peer_read_message_id')) {
            return meta.peer_read_message_id;
        }
        return undefined;
    };
    if (Array.isArray(root.data)) {
        return {
            rows: root.data,
            meta: root.meta || null,
            peerReadMessageId: pickPeer(root),
            shape: 'paginate',
        };
    }
    const inner = root.data;
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
        const bag = inner;
        shape = 'wrapped-data';
        if (Array.isArray(bag.data)) {
            return {
                rows: bag.data,
                meta: bag.meta || root.meta || null,
                peerReadMessageId: pickPeer(bag) ?? pickPeer(root),
                shape,
            };
        }
        if (Array.isArray(bag.messages)) {
            return {
                rows: bag.messages,
                meta: bag.meta || root.meta || null,
                peerReadMessageId: pickPeer(bag) ?? pickPeer(root),
                shape: 'wrapped-messages',
            };
        }
    }
    if (root.status === true || root.status === 1) {
        const statusData = root.data;
        if (Array.isArray(statusData)) {
            return {
                rows: statusData,
                meta: root.meta || null,
                peerReadMessageId: pickPeer(root),
                shape: 'status-array',
            };
        }
    }
    if (Array.isArray(root.messages)) {
        return {
            rows: root.messages,
            meta: root.meta || null,
            peerReadMessageId: pickPeer(root),
            shape: 'messages-key',
        };
    }
    return {
        rows: [],
        meta: root.meta || null,
        peerReadMessageId: pickPeer(root),
        shape: 'unknown',
    };
}
